import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowRight, Flame, Pizza, Soup } from "lucide-react";

/**
 * Reusable SEO landing page. Each route passes a `config` object with the
 * H1, intro copy, keywords, and a list of internal links — Google sees one
 * focused topical page per search intent (e.g. "Indian pizza Lethbridge").
 *
 * Why this works for SEO:
 *   1. Each page has its own <title>, <meta description>, and canonical URL
 *      set dynamically via document.title + meta tags (single-page React
 *      apps don't have server-rendered meta otherwise).
 *   2. The H1 contains the target keyword phrase verbatim — Google heavily
 *      weights H1 alignment with intent.
 *   3. Internal links back to /menu, /feasts, /order anchor the page in the
 *      site graph so PageRank flows correctly.
 */
export default function SeoLanding({ config }) {
  useEffect(() => {
    document.title = config.title;
    setMeta("description", config.description);
    setMeta("keywords", config.keywords);
    setOg("og:title", config.title);
    setOg("og:description", config.description);
    setOg("og:url", `https://rivaaz.ca${config.path}`);
    setCanonical(`https://rivaaz.ca${config.path}`);
    // Inject JSON-LD page-specific Restaurant schema with H1 as @description
    const ldId = "rivaaz-seo-ld";
    const old = document.getElementById(ldId);
    if (old) old.remove();
    const s = document.createElement("script");
    s.id = ldId;
    s.type = "application/ld+json";
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: config.title,
      description: config.description,
      url: `https://rivaaz.ca${config.path}`,
      isPartOf: { "@type": "WebSite", url: "https://rivaaz.ca" },
      mainEntity: { "@id": "https://rivaaz.ca/#restaurant" },
    });
    document.head.appendChild(s);
    return () => {
      const node = document.getElementById(ldId);
      if (node) node.remove();
    };
  }, [config]);

  return (
    <div
      data-testid={`seo-landing-${config.slug}`}
      className="bg-[#FAF8F5] text-[#0E0E1A]"
    >
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-16 md:pt-20 md:pb-24">
        <div className="text-xs tracking-[0.25em] uppercase text-[#C9853E] font-bold mb-4">
          {config.eyebrow}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-4xl">
          {config.h1}
        </h1>
        <p className="font-accent italic text-2xl md:text-3xl text-[#57534E] mt-6 max-w-3xl leading-snug">
          {config.intro}
        </p>

        <div className="flex flex-wrap gap-3 mt-8">
          <Link
            to="/menu"
            data-testid={`seo-cta-menu-${config.slug}`}
            className="inline-flex items-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] text-white font-bold rounded-full px-6 py-3 shadow-[0_10px_25px_-8px_rgba(201,133,62,0.6)] transition-all"
          >
            See the full menu <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="tel:+14039422565"
            data-testid={`seo-cta-call-${config.slug}`}
            className="inline-flex items-center gap-2 border border-[#0E0E1A] hover:bg-[#0E0E1A] hover:text-white text-[#0E0E1A] font-bold rounded-full px-6 py-3 transition-all"
          >
            <Phone className="w-4 h-4" /> +1 (403) 942-2565
          </a>
        </div>
      </section>

      <section className="bg-white border-y border-[#E7E5E4]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {config.highlights.map((h, i) => (
            <div
              key={i}
              className="rounded-3xl bg-[#FAF8F5] border border-[#E7E5E4] p-6"
              data-testid={`seo-highlight-${config.slug}-${i}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#0E0E1A] text-white flex items-center justify-center mb-4">
                {h.icon === "flame" && <Flame className="w-5 h-5" />}
                {h.icon === "pizza" && <Pizza className="w-5 h-5" />}
                {h.icon === "soup" && <Soup className="w-5 h-5" />}
                {h.icon === "clock" && <Clock className="w-5 h-5" />}
                {h.icon === "pin" && <MapPin className="w-5 h-5" />}
              </div>
              <h2 className="font-display text-xl font-extrabold mb-2">{h.title}</h2>
              <p className="text-sm text-[#57534E] leading-relaxed">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <h2 className="font-display text-3xl md:text-4xl font-extrabold mb-6 max-w-3xl">
          {config.bodyHeading}
        </h2>
        <div className="prose prose-stone max-w-3xl text-[#57534E] leading-relaxed">
          {config.body.split("\n\n").map((p, i) => (
            <p key={i} className="mb-4 text-base">
              {p}
            </p>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          <Link
            to="/menu"
            className="rounded-2xl bg-[#0E0E1A] text-white p-5 hover:bg-[#262533] transition-all"
            data-testid={`seo-link-menu-${config.slug}`}
          >
            <div className="text-xs tracking-[0.2em] uppercase text-[#C9853E] font-bold">
              Full Menu
            </div>
            <div className="font-display text-lg font-bold mt-1">
              149 dishes across 16 categories →
            </div>
          </Link>
          <Link
            to="/feasts"
            className="rounded-2xl bg-white border border-[#E7E5E4] p-5 hover:border-[#C9853E] transition-all"
            data-testid={`seo-link-feasts-${config.slug}`}
          >
            <div className="text-xs tracking-[0.2em] uppercase text-[#C9853E] font-bold">
              Combo Feasts
            </div>
            <div className="font-display text-lg font-bold mt-1 text-[#0E0E1A]">
              Build a meal for 2, 4 or 6 →
            </div>
          </Link>
        </div>
      </section>

      <section className="bg-[#0E0E1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold mb-4">
            Hungry? We&apos;re a few clicks away.
          </h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8 text-base">
            Tap below to order online for pickup or local delivery, or call us
            directly. Rivaaz Indian Pizza & Cuisine, 1020 Mayor Magrath Drive S,
            Lethbridge.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/order"
              data-testid={`seo-cta-order-${config.slug}`}
              className="inline-flex items-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] text-white font-bold rounded-full px-7 py-4 shadow-[0_10px_25px_-8px_rgba(201,133,62,0.6)] transition-all"
            >
              Order Now <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://maps.google.com/?q=1020+Mayor+Magrath+Drive+South+Lethbridge+AB"
              target="_blank"
              rel="noreferrer"
              data-testid={`seo-cta-directions-${config.slug}`}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full px-7 py-4 transition-all"
            >
              <MapPin className="w-4 h-4" /> Get directions
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function setMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setOg(property, content) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(url) {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", url);
}
